/**
 * Purchase Orders — unified manager for MANUAL + MRP_DRAFT POs.
 *
 * Feb 2026 Phase 2. Replaces the split "MRP Draft POs" page as the
 * single entry point for all POs. Filters (Status / Vendor / Branch /
 * PO Type / PO Number search) apply to both the list AND the top-line
 * summary cards so they can never disagree.
 *
 * "Approval Queue" is a preset tab that just sets `status=PENDING_APPROVAL`.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Eye, RefreshCw, ShoppingCart, Clock, CheckCircle, AlertTriangle, Ban, IndianRupee } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import axios from 'axios';
import PurchaseOrderCreateDialog from '../components/PurchaseOrderCreateDialog';
import PurchaseOrderDetailDialog from '../components/PurchaseOrderDetailDialog';
import useAuthStore from '../store/authStore';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  DRAFT: 'bg-gray-100 text-gray-800',
  PENDING_APPROVAL: 'bg-yellow-100 text-yellow-800',
  OPEN: 'bg-blue-100 text-blue-800',
  PARTIAL: 'bg-indigo-100 text-indigo-800',
  CLOSED: 'bg-green-100 text-green-800',
  SHORT_CLOSED: 'bg-orange-100 text-orange-800',
  CANCELLED: 'bg-zinc-200 text-zinc-700',
  REJECTED: 'bg-red-100 text-red-800',
  DELETED: 'bg-rose-100 text-rose-800 line-through',
};

const STATUS_OPTIONS = [
  'DRAFT', 'PENDING_APPROVAL', 'OPEN', 'PARTIAL',
  'CLOSED', 'SHORT_CLOSED', 'CANCELLED', 'REJECTED', 'DELETED',
];

const TAB_TO_STATUS = {
  all: '',
  approval_queue: 'PENDING_APPROVAL',
};

function fmtMoney(v) {
  const n = parseFloat(v || 0);
  return '₹' + n.toLocaleString('en-IN', { maximumFractionDigits: 0 });
}
function fmtDate(v) {
  if (!v) return '—';
  try { return new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return v; }
}

export default function PurchaseOrders() {
  const { token } = useAuthStore();
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }),
    [token],
  );

  const [tab, setTab] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [branchFilter, setBranchFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const [pos, setPOs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [settings, setSettings] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [editingPO, setEditingPO] = useState(null);   // present → dialog is in edit mode
  const [detailPO, setDetailPO] = useState(null);

  const effectiveStatus =
    tab === 'approval_queue' ? 'PENDING_APPROVAL' :
    statusFilter !== 'all' ? statusFilter : '';

  // Fetch list + summary — both respect the same filter set so cards
  // and table can never disagree.
  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    const params = {};
    if (effectiveStatus) params.status = effectiveStatus;
    if (vendorFilter !== 'all') params.vendor_id = vendorFilter;
    if (branchFilter !== 'all') params.branch_id = branchFilter;
    if (typeFilter !== 'all') params.po_type = typeFilter;
    try {
      const [listRes, sumRes] = await Promise.all([
        axios.get(`${API}/api/purchase-orders`, { headers, params }),
        axios.get(`${API}/api/purchase-orders/summary`, {
          headers,
          params: {
            ...(vendorFilter !== 'all' && { vendor_id: vendorFilter }),
            ...(branchFilter !== 'all' && { branch_id: branchFilter }),
            ...(typeFilter !== 'all' && { po_type: typeFilter }),
          },
        }),
      ]);
      setPOs(Array.isArray(listRes.data) ? listRes.data : []);
      setSummary(sumRes.data);
    } catch (err) {
      toast.error('Failed to load purchase orders');
    }
    setLoading(false);
  }, [token, headers, effectiveStatus, vendorFilter, branchFilter, typeFilter]);

  // One-time master data load
  useEffect(() => {
    if (!token) return;
    Promise.all([
      axios.get(`${API}/api/vendors`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${API}/api/branches`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${API}/api/purchase-orders/settings`, { headers }).catch(() => ({ data: null })),
    ]).then(([vRes, bRes, sRes]) => {
      setVendors(Array.isArray(vRes.data) ? vRes.data : []);
      setBranches(Array.isArray(bRes.data) ? bRes.data : []);
      setSettings(sRes.data);
    });
  }, [token, headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Filtered by search term (client-side — po_number substring).
  const filteredPOs = useMemo(() => {
    if (!searchTerm.trim()) return pos;
    const q = searchTerm.trim().toLowerCase();
    return pos.filter(
      (p) =>
        (p.po_number || '').toLowerCase().includes(q) ||
        (p.vendor_name || '').toLowerCase().includes(q),
    );
  }, [pos, searchTerm]);

  // Billing/shipping location dropdown options — pull from branches.
  const branchLocations = useMemo(
    () => branches.map((b) => b.name).filter(Boolean),
    [branches],
  );

  const openDetail = async (po) => {
    // Refetch to get the latest lines + flags before opening detail.
    try {
      const res = await axios.get(`${API}/api/purchase-orders/${po.id}`, { headers });
      setDetailPO(res.data);
    } catch {
      setDetailPO(po);
    }
  };

  const onCreateSaved = () => {
    setShowCreate(false);
    setEditingPO(null);
    fetchAll();
  };

  const onDetailChanged = (updated) => {
    setDetailPO(updated);
    fetchAll();
  };

  const openEditFromDetail = (po) => {
    setDetailPO(null);
    setEditingPO(po);
    setShowCreate(true);
  };

  return (
    <div className="p-6 space-y-4" data-testid="purchase-orders-page">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Purchase Orders
          </h1>
          <p className="text-zinc-500 text-sm">
            Unified list of manual and MRP-generated purchase orders.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchAll} disabled={loading} data-testid="po-refresh-btn">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button onClick={() => { setEditingPO(null); setShowCreate(true); }} data-testid="po-create-btn">
            <Plus className="h-4 w-4 mr-2" />Create New PO
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="po-summary-cards">
          <SummaryCard label="Draft" value={summary.by_status?.DRAFT || 0} icon={ShoppingCart} testid="po-summary-draft" />
          <SummaryCard label="Pending Approval" value={summary.by_status?.PENDING_APPROVAL || 0} icon={Clock} testid="po-summary-pending" />
          <SummaryCard label="Open" value={summary.by_status?.OPEN || 0} icon={CheckCircle} testid="po-summary-open" />
          <SummaryCard label="Partial" value={summary.by_status?.PARTIAL || 0} icon={AlertTriangle} testid="po-summary-partial" />
          <SummaryCard label="Closed / Short" value={(summary.by_status?.CLOSED || 0) + (summary.by_status?.SHORT_CLOSED || 0)} icon={Ban} testid="po-summary-closed" />
          <SummaryCard label="Open Value" value={fmtMoney(summary.open_value)} icon={IndianRupee} testid="po-summary-open-value" money />
        </div>
      )}

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="po-tabs">
          <TabsTrigger value="all" data-testid="po-tab-all">All POs</TabsTrigger>
          <TabsTrigger value="approval_queue" data-testid="po-tab-approval">Approval Queue</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <Input
              placeholder="Search PO # or vendor…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="po-search-input"
            />
            {tab !== 'approval_queue' && (
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="po-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger data-testid="po-type-filter"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="RM">RM</SelectItem>
                <SelectItem value="FG">FG</SelectItem>
                <SelectItem value="JOBWORK">JOBWORK</SelectItem>
                <SelectItem value="EXPENSE">EXPENSE</SelectItem>
              </SelectContent>
            </Select>
            <Select value={vendorFilter} onValueChange={setVendorFilter}>
              <SelectTrigger data-testid="po-vendor-filter"><SelectValue placeholder="Vendor" /></SelectTrigger>
              <SelectContent className="max-h-64">
                <SelectItem value="all">All vendors</SelectItem>
                {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={branchFilter} onValueChange={setBranchFilter}>
              <SelectTrigger data-testid="po-branch-filter"><SelectValue placeholder="Branch" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All branches</SelectItem>
                {branches.map((b) => <SelectItem key={b.id || b.name} value={b.id || b.name}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="pt-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>PO #</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Branch</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Order Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Source</TableHead>
                <TableHead className="text-right">Grand Total</TableHead>
                <TableHead className="w-[80px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPOs.map((p) => (
                <TableRow key={p.id} data-testid={`po-row-${p.po_number}`}>
                  <TableCell className="font-mono text-sm">{p.po_number}</TableCell>
                  <TableCell className="max-w-[200px] truncate" title={p.vendor_name}>{p.vendor_name}</TableCell>
                  <TableCell>{p.branch}</TableCell>
                  <TableCell><Badge variant="outline">{p.po_type}</Badge></TableCell>
                  <TableCell>{fmtDate(p.order_date)}</TableCell>
                  <TableCell><Badge className={STATUS_STYLES[p.status] || 'bg-gray-100'}>{p.status}</Badge></TableCell>
                  <TableCell><Badge variant="outline">{p.source?.kind || 'MANUAL'}</Badge></TableCell>
                  <TableCell className="text-right font-mono">{fmtMoney(p.totals?.grand_total)}</TableCell>
                  <TableCell>
                    <Button size="sm" variant="ghost" onClick={() => openDetail(p)} data-testid={`po-view-${p.po_number}`}>
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {filteredPOs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-zinc-500" data-testid="po-empty-state">
                    No purchase orders match the current filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <PurchaseOrderCreateDialog
        open={showCreate}
        onClose={() => { setShowCreate(false); setEditingPO(null); }}
        onSaved={onCreateSaved}
        initialPo={editingPO}
        branches={branches}
        billingLocations={branchLocations}
        shippingLocations={branchLocations}
        settings={settings}
      />
      <PurchaseOrderDetailDialog
        open={!!detailPO}
        po={detailPO}
        onClose={() => setDetailPO(null)}
        onChange={onDetailChanged}
        onEdit={openEditFromDetail}
      />
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, money = false, testid }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-zinc-500 uppercase tracking-wide">{label}</p>
            <p className={`text-xl font-bold mt-1 ${money ? 'text-blue-700' : ''}`} data-testid={testid}>
              {value}
            </p>
          </div>
          {Icon && <Icon className="h-6 w-6 text-zinc-400" />}
        </div>
      </CardContent>
    </Card>
  );
}
